import Vue from 'vue';
import { ValidationProvider, ValidationObserver, extend } from 'vee-validate';
import {
  required,
  email,
  confirmed,
  is,
  is_not,
  regex,
  min,
  min_value,
  max,
  max_value,
  integer,
  oneOf,
  length,
} from 'vee-validate/dist/rules';

import { PhoneNumberUtil } from 'google-libphonenumber';
import moment from 'moment';

import i18n from '@/plugins/i18n';

extend('required', {
  ...required,
  message: (_, values) => i18n.t('commons.validations.required', values) as string,
});

extend('email', {
  ...email,
  message: (_, values) => i18n.t('commons.validations.email', values) as string,
});

extend('confirmed', confirmed);
extend('is', is);
extend('regex', regex);
extend('min', min);
extend('min_value', min_value);
extend('max', max);
extend('max_value', max_value);
extend('integer', integer);
extend('oneOf', oneOf);
extend('length', {
  ...length,
  message: (_, values) => i18n.tc('commons.validations.length', values!.length) as string,
});

extend('checkbox', {
  message: (_, values) => i18n.t('commons.validations.checkbox-required', values) as string,
  validate: (value: boolean) => {
    return value;
  },
});

extend('date', {
  message: (_, values) => i18n.t('commons.validations.date-invalid', values) as string,
  validate: (value: string) => {
    return moment(value, Vue.prototype.$pdc.helper.dateFormatRelatedToSelectedLanguage, true).isValid();
  },
});

extend('birthDate', {
  message: (_, values) => i18n.t('commons.validations.date-invalid', values) as string,
  validate: (value: string) => {
    const isValid = moment(value, Vue.prototype.$pdc.helper.dateFormatRelatedToSelectedLanguage, true).isValid();
    if (isValid) {
      const before = moment(value, Vue.prototype.$pdc.helper.dateFormatRelatedToSelectedLanguage).diff(
        moment().subtract(120, 'years'),
        'days',
      );
      const now = moment().diff(moment(value, Vue.prototype.$pdc.helper.dateFormatRelatedToSelectedLanguage), 'days');

      return Math.sign(before) >= 0 && Math.sign(now) >= 0;
    }
    return false;
  },
});

extend('array_min', {
  message: (_, values) => i18n.t('commons.validations.array-max', values) as string,
  validate: (value: any, min) => {
    return value.length >= min;
  },
});

extend('array_max', {
  message: (_, values) => i18n.t('commons.validations.array-max', values) as string,
  validate: (value: any, max) => {
    return value.length <= max;
  },
});

extend('UsernameExistsException', {
  ...is_not,
  message: (_) => i18n.t('errors.UsernameExistsException') as string,
});

extend('password', {
  validate: (value: string) => {
    return /^((?=\S*?[A-Z])(?=\S*?[a-z])(?=\S*?[^A-Za-z0-9\s])(?=\S*?[0-9]).{7,})\S$/.test(value);
  },
  message: (_) => i18n.t('errors.password-not-strong-enough') as string,
});

extend('phone', {
  validate: (value: any, country: any) => {
    try {
      const _vdt = PhoneNumberUtil.getInstance();
      const number = _vdt.parseAndKeepRawInput(Array.isArray(value) ? value[0] : value, country[0]);
      return PhoneNumberUtil.getInstance().isValidNumberForRegion(number, country[0]);
    } catch (_) {
      return false;
    }
  },
  message: (_) => i18n.t('errors.invalid-phone') as string,
});

Vue.component('ValidationProvider', ValidationProvider);
Vue.component('ValidationObserver', ValidationObserver);
